import { useState, type ReactNode } from 'react';
import { Kicker } from '@/components/Kicker';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Input } from '@/components/ui/input';

/** A count in an impact line: "<strong>3</strong> build profiles", "… build profiles" while it loads. */
export function Counted({ n, one, many }: { n: number | undefined; one: string; many: string }) {
  return (
    <>
      <strong>{n ?? '…'}</strong> {n === 1 ? one : many}
    </>
  );
}

export interface ImpactItem {
  /** One line; put the count in <strong> ("<strong>3</strong> build profiles"). */
  text: ReactNode;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  /** What changes, one line each (the modal-vs-page rule: a destructive action lists its impact). */
  impact: ImpactItem[];
  confirmLabel: string;
  busy?: boolean;
  /** Receives the typed confirmation (trimmed) when there is one. */
  onConfirm: (typed: string) => void;
  testId: string;
  /** Typed to confirm (design 28's danger zone: the project slug); the action stays disabled until it matches. */
  confirmText?: string;
  /** An option of the same decision (e.g. "archive its apps too"), under the impact summary. */
  children?: ReactNode;
}

/**
 * The confirm step of every archive and disable (project, app, profile, provider account): one decision, an impact
 * summary, nothing deleted. Design 28 · Danger zone: the summary box with red bullets and, for a project, the slug
 * typed to confirm.
 */
export function ConfirmArchiveDialog(props: Props) {
  return (
    <AlertDialog open={props.open} onOpenChange={props.onOpenChange}>
      <AlertDialogContent data-testid={props.testId}>{props.open ? <ConfirmBody {...props} /> : null}</AlertDialogContent>
    </AlertDialog>
  );
}

function ImpactSummary({ impact }: { impact: ImpactItem[] }) {
  return (
    <div className="rounded-lg border bg-background/60 px-3.5 py-3">
      <Kicker className="text-[11px] text-subtle">Impact summary</Kicker>
      <ul className="mt-2 flex flex-col gap-1.5 text-[13px] text-muted-foreground [&_strong]:font-semibold [&_strong]:text-foreground" aria-label="Impact summary">
        {impact.map((item, i) => (
          <li key={i} className="flex items-start gap-2">
            <span aria-hidden className="mt-[7px] size-1.5 shrink-0 rounded-full bg-destructive" />
            <span className="min-w-0">{item.text}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TypeToConfirmProps {
  confirmText: string;
  testId: string;
  typed: string;
  matches: boolean;
  onTyped: (value: string) => void;
}

function TypeToConfirm({ confirmText, testId, typed, matches, onTyped }: TypeToConfirmProps) {
  return (
    <label className="flex flex-col gap-1.5 text-[13px] text-muted-foreground">
      <span>
        Type <code className="rounded-sm bg-destructive/10 px-1.5 py-px font-mono text-xs text-destructive">{confirmText}</code> to confirm
      </span>
      <Input data-testid={`${testId}-type`} value={typed} onChange={(e) => onTyped(e.target.value)} autoComplete="off" spellCheck={false} className="font-mono sm:text-[13px]" aria-invalid={typed.length > 0 && !matches ? true : undefined} />
    </label>
  );
}

function ConfirmBody({ title, description, impact, confirmLabel, busy, onConfirm, testId, confirmText, children }: Props) {
  const [typed, setTyped] = useState('');
  const matches = !confirmText || typed.trim() === confirmText;
  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <ImpactSummary impact={impact} />
      {children}
      {confirmText ? <TypeToConfirm confirmText={confirmText} testId={testId} typed={typed} matches={matches} onTyped={setTyped} /> : null}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-dismiss`}>Keep it</AlertDialogCancel>
        <AlertDialogAction
          destructive
          data-testid={`${testId}-confirm`}
          disabled={busy || !matches}
          onClick={(e) => {
            e.preventDefault();
            if (matches) onConfirm(typed.trim());
          }}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </>
  );
}
